import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown } from '../../../svgs/svgs';

interface PopoverProps {
  className?: string;
  customTrigger?: React.ReactNode;
  trigger?: React.ReactNode;
  content?: ((close: () => void) => React.ReactNode) | React.ReactNode | null;
  customContent?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  withArrow?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export default function Popover({
  className = '',
  customTrigger = null,
  trigger = null,
  content = null,
  customContent = false,
  placement = 'bottom',
  withArrow = false,
  size = 'md',
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const closePopover = () => setOpen(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    finalPlacement: placement,
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Size mapping for popover content
  const sizeClasses = {
    xs: 'min-w-[120px] max-w-[200px]',
    sm: 'min-w-[150px] max-w-[250px]',
    md: 'min-w-[200px] max-w-[350px]',
    lg: 'min-w-[300px] max-w-[500px]',
    xl: 'min-w-[400px] max-w-[650px]',
  };

  // Size mapping for trigger button
  const triggerSizeClasses = {
    xs: 'h-[32px] px-2 py-1 text-sm',
    sm: 'h-[36px] px-2 py-1',
    md: 'h-[44px] px-2 py-1',
    lg: 'h-[52px] px-3 py-2 text-lg',
    xl: 'h-[60px] px-4 py-2 text-xl',
  };

  const calculatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const popoverWidth = 300; // Approximate max width for calculations

    let top, left;
    let finalPlacement = placement;

    const spaceAbove = rect.top;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    // Auto flip logic
    if (placement === 'bottom' && spaceBelow < 150 && spaceAbove > spaceBelow) {
      finalPlacement = 'top';
    } else if (
      placement === 'top' &&
      spaceAbove < 150 &&
      spaceBelow > spaceAbove
    ) {
      finalPlacement = 'bottom';
    } else if (
      placement === 'left' &&
      spaceLeft < 150 &&
      spaceRight > spaceLeft
    ) {
      finalPlacement = 'right';
    } else if (
      placement === 'right' &&
      spaceRight < 150 &&
      spaceLeft > spaceRight
    ) {
      finalPlacement = 'left';
    }

    const baseTop = rect.top + window.scrollY;
    const baseLeft = rect.left + window.scrollX;

    switch (finalPlacement) {
      case 'top':
        top = baseTop - gap;
        left = baseLeft;
        break;
      case 'bottom':
        top = baseTop + rect.height + gap;
        left = baseLeft;
        break;
      case 'left':
        top = baseTop + rect.height / 2;
        left = baseLeft - gap;
        break;
      case 'right':
        top = baseTop + rect.height / 2;
        left = baseLeft + rect.width + gap;
        break;
      default:
        top = baseTop + rect.height + gap;
        left = baseLeft;
    }

    // Horizontal boundary checks for top/bottom placements
    if (finalPlacement === 'top' || finalPlacement === 'bottom') {
      const popoverRightEdge = left + popoverWidth;
      const popoverLeftEdge = left;

      // If popover would overflow right edge, align it to the right
      if (popoverRightEdge > viewportWidth) {
        left = viewportWidth - popoverWidth - 10; // 10px padding from edge
      }

      // If popover would overflow left edge, align it to the left
      if (popoverLeftEdge < 0) {
        left = 10; // 10px padding from edge
      }
    }

    setPosition({ top, left, finalPlacement });
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    if (!open) {
      calculatePosition();
    }
    setOpen((prev) => !prev);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      triggerRef.current &&
      !triggerRef.current.contains(e.target as Node) &&
      popoverRef.current &&
      !popoverRef.current.contains(e.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', calculatePosition);

    // Track trigger position on every frame so any layout shift (e.g. a
    // sibling appearing) keeps the popover correctly anchored.
    let rafId: number;
    let prevTop = 0;
    let prevLeft = 0;

    const trackPosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        if (rect.top !== prevTop || rect.left !== prevLeft) {
          prevTop = rect.top;
          prevLeft = rect.left;
          calculatePosition();
        }
      }
      rafId = requestAnimationFrame(trackPosition);
    };

    rafId = requestAnimationFrame(trackPosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', calculatePosition);
      cancelAnimationFrame(rafId);
    };
  }, [open]);

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleToggle}
        className={`${className} inline-flex items-center gap-1 cursor-pointer select-none`}
      >
        {trigger ? (
          <button
            className={`bg-modal-input border border-modal-stroke rounded-lg flex items-center gap-2 ${triggerSizeClasses[size] || triggerSizeClasses.md}`}
          >
            {trigger}
            {withArrow && (
              <div
                className={`transition-transform duration-300 text-text-primary-body ${
                  open ? 'rotate-180' : 'rotate-0'
                }`}
              >
                <ArrowDown size={16} />
              </div>
            )}
          </button>
        ) : (
          <>{customTrigger}</>
        )}
      </div>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            id="popover-content"
            className={`absolute z-[9999]
        bg-frame-inner border border-frame-stroke 
        text-text-primary-body rounded-xl 
        shadow-lg p-3
        animate-fade-in
        ${!customContent ? sizeClasses[size] || sizeClasses.md : ''}`}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform:
                position.finalPlacement === 'left'
                  ? 'translate(-100%, -50%)'
                  : position.finalPlacement === 'right'
                    ? 'translate(0, -50%)'
                    : position.finalPlacement === 'top'
                      ? 'translate(0, -100%)'
                      : 'translate(0, 0)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {typeof content === 'function' ? content(closePopover) : content}
          </div>,
          document.body
        )}
    </>
  );
}
